import {Button, HStack, Input, Stack, Textarea, Field} from "@chakra-ui/react";
import {useState} from "react";
import api from "../../api/index.ts";
import {Team, TeamMin} from "platformed-client/types";
import {useValidatedPromiseState, useValidation} from "../../hooks/validationState.ts";
import {withDialog} from "../../state/withDialog.tsx";
import {DialogBody, DialogFooter, DialogHeader, DialogTitle} from "../../components/ui/dialog.tsx";
import {Alert} from "../../components/ui/alert.tsx";
import T from "platformed-components/i18n/T";

export const CreateEditTeamDialog = withDialog(
  ({
    type,
    initialName,
    team,
    onClose,
    onCreate,
  }: {
    type: "Create" | "Edit";
    initialName?: string;
    onClose: () => void;
    team?: Team;
    onCreate?: (team: TeamMin) => Promise<unknown>;
  }) => {
    const create = type === "Create" && !team;

    const [name, setName] = useState(create ? (initialName ?? "") : team!.name);
    const [description, setDescription] = useState(create ? "" : team!.description);
    const [validationErrors, setValidationErrors] = useValidation(useState({}), {name});

    const [creatingOrUpdating, createOrUpdate] = useValidatedPromiseState(
      async () => {
        if (create) {
          const team = await api.teams.create({name, description});
          onCreate && (await onCreate(team));
        } else {
          await api.teams.updateName(team!.team_id, name);
          if (description) {
            await api.teams.updateDescription(team!.team_id, description);
          }
        }

        onClose();
      },
      [create, onClose, name, description, onCreate, team],
      setValidationErrors,
    );

    return (
      <form onSubmit={createOrUpdate}>
        <DialogHeader>
          <DialogTitle>{create ? <T keyName="c.Create_team" /> : <T keyName="c.Edit_team" />}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap={6}>
            <Field.Root required invalid={validationErrors.name !== undefined}>
              <Field.Label>
                <T keyName="c.Name" />
              </Field.Label>
              <Input value={name} onChange={e => setName(e.target.value)} autoFocus />
              {validationErrors.name && validationErrors.name[0] && (
                <Field.ErrorText>{validationErrors.name[0].message}</Field.ErrorText>
              )}
            </Field.Root>
            <Field.Root>
              <Field.Label>
                <T keyName="c.Description" />
              </Field.Label>
              <Textarea value={description ?? undefined} onChange={e => setDescription(e.target.value)} />
            </Field.Root>
            {creatingOrUpdating.lastError ? (
              <Alert status="error" mt="12" title={<T keyName="c.Error_creating_team" />}>
                {`${creatingOrUpdating.lastError}`}
              </Alert>
            ) : null}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <HStack gap="3">
            <Button variant="outline" onClick={onClose}>
              <T keyName="c.Cancel" />
            </Button>
            <Button
              colorPalette="blue"
              type={"submit"}
              disabled={creatingOrUpdating.inProgress}
              loading={creatingOrUpdating.inProgress}
            >
              {create ? <T keyName="c.Create" /> : <T keyName="c.Save" />}
            </Button>
          </HStack>
        </DialogFooter>
      </form>
    );
  },
);
